import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, AlertTriangle, Lock, UserPlus, LogIn, MessageSquare, Inbox, ShieldCheck, Zap } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useAdminStore } from '@/lib/admin-store';
import { useUserStore } from '@/lib/user-store';
import { generateId } from '@/lib/slug-utils';
import toast from 'react-hot-toast';

const INPUT_CLS = "w-full px-4 py-3.5 rounded-xl border border-black/10 dark:border-white/10 bg-transparent text-sm font-medium placeholder:text-black/30 dark:placeholder:text-white/30 focus:outline-none focus:border-black/30 dark:focus:border-white/30 transition-colors";

// --- Spam protection constants ---
const RATE_LIMIT_KEY = 'gallery-contact-timestamps';
const MAX_MESSAGES = 3;            // max messages per window
const RATE_WINDOW_MS = 5 * 60_000; // 5-minute window
const MIN_SUBMIT_GAP_MS = 30_000;  // 30s min between submissions
const MAX_NAME_LEN = 100;
const MAX_EMAIL_LEN = 254;
const MAX_SUBJECT_LEN = 200;
const MAX_MESSAGE_LEN = 2000;
const MIN_MESSAGE_LEN = 10;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function getSubmitTimestamps(): number[] {
  try {
    const raw = localStorage.getItem(RATE_LIMIT_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as number[];
  } catch { return []; }
}

function recordSubmit(): void {
  const stamps = getSubmitTimestamps();
  stamps.push(Date.now());
  // Keep only timestamps within the rate window
  const cutoff = Date.now() - RATE_WINDOW_MS;
  const pruned = stamps.filter((t) => t > cutoff);
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(pruned));
}

function getRateLimitInfo(): { allowed: boolean; remainingMessages: number; cooldownSeconds: number } {
  const now = Date.now();
  const cutoff = now - RATE_WINDOW_MS;
  const stamps = getSubmitTimestamps().filter((t) => t > cutoff);
  const remaining = MAX_MESSAGES - stamps.length;
  const lastStamp = stamps.length > 0 ? stamps[stamps.length - 1]! : 0;
  const timeSinceLast = now - lastStamp;
  const gapCooldown = lastStamp > 0 && timeSinceLast < MIN_SUBMIT_GAP_MS
    ? Math.ceil((MIN_SUBMIT_GAP_MS - timeSinceLast) / 1000)
    : 0;

  if (remaining <= 0) {
    // Calculate when oldest timestamp expires
    const oldest = stamps[0]!;
    const windowCooldown = Math.ceil((oldest + RATE_WINDOW_MS - now) / 1000);
    return { allowed: false, remainingMessages: 0, cooldownSeconds: Math.max(windowCooldown, 0) };
  }

  if (gapCooldown > 0) {
    return { allowed: false, remainingMessages: remaining, cooldownSeconds: gapCooldown };
  }

  return { allowed: true, remainingMessages: remaining, cooldownSeconds: 0 };
}

const ContactPage: React.FC = () => {
  const { t, localize } = useTranslation();
  const [searchParams] = useSearchParams();
  const settings = useAdminStore((s) => s.settings);
  const allArtworks = useAdminStore((s) => s.artworks);
  const addConversation = useAdminStore((s) => s.addConversation);
  const currentUser = useUserStore((s) => s.currentUser);

  // Pre-fill from query params (e.g. custom frame inquiry)
  const isCustomFrame = searchParams.get('subject') === 'custom-frame';
  const artworkSlug = searchParams.get('artwork');
  const artwork = artworkSlug ? allArtworks.find((a) => a.slug === artworkSlug) : null;

  const defaultSubject = useMemo(() => {
    if (isCustomFrame && artwork) {
      return `${t.contact.customFrameSubject} — ${localize(artwork.title)}`;
    }
    if (isCustomFrame) {
      return t.contact.customFrameSubject;
    }
    return '';
  }, [isCustomFrame, artwork, t, localize]);

  const [name, setName] = useState(currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timer); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Check rate limit on mount
  useEffect(() => {
    const info = getRateLimitInfo();
    if (!info.allowed) setCooldown(info.cooldownSeconds);
  }, []);

  const validate = useCallback((): boolean => {
    const e: Record<string, string> = {};
    const trimName = name.trim();
    const trimEmail = email.trim();
    const trimSubject = subject.trim();
    const trimMsg = message.trim();

    if (!trimName) e.name = 'Required';
    else if (trimName.length > MAX_NAME_LEN) e.name = `Max ${MAX_NAME_LEN} characters`;

    if (!trimEmail) e.email = 'Required';
    else if (trimEmail.length > MAX_EMAIL_LEN) e.email = `Max ${MAX_EMAIL_LEN} characters`;
    else if (!EMAIL_RE.test(trimEmail)) e.email = 'Invalid email';

    if (!trimSubject) e.subject = 'Required';
    else if (trimSubject.length > MAX_SUBJECT_LEN) e.subject = `Max ${MAX_SUBJECT_LEN} characters`;

    if (!trimMsg) e.message = 'Required';
    else if (trimMsg.length < MIN_MESSAGE_LEN) e.message = `Min ${MIN_MESSAGE_LEN} characters`;
    else if (trimMsg.length > MAX_MESSAGE_LEN) e.message = `Max ${MAX_MESSAGE_LEN} characters`;

    setErrors(e);
    return Object.keys(e).length === 0;
  }, [name, email, subject, message]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!validate()) return;

    // Rate limit check
    const rateInfo = getRateLimitInfo();
    if (!rateInfo.allowed) {
      setCooldown(rateInfo.cooldownSeconds);
      toast.error(t.contact.rateLimited);
      return;
    }

    if (sending) return;
    setSending(true);
    try {

    const now = new Date().toISOString();
    const firstMessage = {
      _id: generateId(),
      conversationId: '',
      senderType: currentUser ? 'user' as const : 'guest' as const,
      senderName: name.trim(),
      senderEmail: email.trim(),
      content: message.trim(),
      read: false,
      createdAt: now,
    };

    await addConversation({
      userId: currentUser?._id,
      guestName: name.trim(),
      guestEmail: email.trim(),
      subject: subject.trim(),
      source: 'contact',
      messages: [firstMessage],
      status: 'open',
      lastMessageAt: now,
    });

    recordSubmit();
    const updatedInfo = getRateLimitInfo();
    if (!updatedInfo.allowed) setCooldown(updatedInfo.cooldownSeconds);

    toast.success(t.inbox.contactSuccess);
    setName(currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : '');
    setEmail(currentUser?.email || '');
    setSubject('');
    setMessage('');
    setErrors({});
    setSending(false);
    } catch { toast.error(t.nav.home !== 'Home' ? 'გაგზავნა ვერ მოხერხდა. სცადეთ ხელახლა.' : 'Could not send. Please try again.'); }
    finally { setSending(false); }
  };

  const isDisabled = sending || cooldown > 0;

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-20 px-4 sm:px-6 md:px-24">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-10 sm:mb-16"
        >
          <h1 className="text-3xl sm:text-5xl md:text-8xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
            {t.nav.contact}
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="space-y-6"
          >
            {[
              { icon: MapPin, label: 'Address', value: localize(settings?.contactInfo?.address) || '12 Rustaveli Avenue, Tbilisi, Georgia' },
              { icon: Mail, label: 'Email', value: settings?.contactInfo?.email || 'info@fotoatelier.ge' },
              { icon: Phone, label: 'Phone', value: settings?.contactInfo?.phone || '+995 32 123 4567' },
              { icon: Clock, label: 'Hours', value: localize(settings?.contactInfo?.hours) || 'Tue – Sun, 11:00 – 19:00' },
            ].filter((item) => item.value).map((item) => (
              <div
                key={item.label}
                className="flex items-start gap-4 p-5 rounded-2xl border border-black/5 dark:border-white/5 bg-[#fafafa] dark:bg-[#0a0a0a] transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center shrink-0">
                  <item.icon size={18} className="text-black/40 dark:text-white/40" />
                </div>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-1">
                    {item.label}
                  </p>
                  <p className="text-sm font-medium text-black/70 dark:text-white/70">
                    {item.value}
                  </p>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Contact Form or Registration Required */}
          <AnimatePresence mode="wait">
            {!currentUser ? (
              /* ── Registration Required Notification ── */
              <motion.div
                key="auth-required"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="relative overflow-hidden rounded-3xl border border-black/[0.06] dark:border-white/[0.06] bg-gradient-to-br from-[#fafafa] via-white to-[#f5f5f5] dark:from-[#0a0a0a] dark:via-[#111] dark:to-[#0a0a0a]"
              >
                {/* Decorative background elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
                    className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-gradient-to-br from-black/[0.02] to-transparent dark:from-white/[0.02]"
                  />
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ duration: 80, repeat: Infinity, ease: 'linear' }}
                    className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-gradient-to-tr from-black/[0.02] to-transparent dark:from-white/[0.02]"
                  />
                </div>

                <div className="relative p-8 sm:p-10 flex flex-col items-center text-center space-y-6">
                  {/* Lock icon with pulse */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5, type: 'spring', stiffness: 200, damping: 15 }}
                    className="relative"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
                      <Lock size={28} className="text-black/50 dark:text-white/50" />
                    </div>
                    <motion.div
                      animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0, 0.3] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute inset-0 rounded-2xl border border-black/10 dark:border-white/10"
                    />
                  </motion.div>

                  {/* Title */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    className="space-y-3"
                  >
                    <h3 className="text-lg sm:text-xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tight">
                      {t.contact.loginRequired}
                    </h3>
                    <p className="text-sm text-black/50 dark:text-white/50 font-light leading-relaxed max-w-sm">
                      {t.contact.loginRequiredDesc}
                    </p>
                  </motion.div>

                  {/* Why register reasons */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                    className="w-full max-w-xs space-y-2.5"
                  >
                    <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25 mb-3">
                      {t.contact.whyRegister}
                    </p>
                    {[
                      { icon: Inbox, text: t.contact.whyRegisterReasons[0] },
                      { icon: MessageSquare, text: t.contact.whyRegisterReasons[1] },
                      { icon: Zap, text: t.contact.whyRegisterReasons[2] },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.9 + i * 0.1, duration: 0.5 }}
                        className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-black/[0.02] dark:bg-white/[0.03] text-left"
                      >
                        <div className="w-7 h-7 rounded-lg bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center shrink-0">
                          <item.icon size={13} className="text-black/40 dark:text-white/40" />
                        </div>
                        <span className="text-xs font-medium text-black/60 dark:text-white/60">
                          {item.text}
                        </span>
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* CTA Buttons */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.6 }}
                    className="flex flex-col sm:flex-row gap-3 w-full max-w-xs pt-2"
                  >
                    <Link
                      to="/register"
                      className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:opacity-90 active:scale-[0.98] transition-all"
                    >
                      <UserPlus size={14} />
                      {t.contact.createAccount}
                    </Link>
                    <Link
                      to="/login"
                      className="flex-1 flex items-center justify-center gap-2 py-3.5 border border-black/10 dark:border-white/10 text-black/70 dark:text-white/70 text-[10px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-black/[0.03] dark:hover:bg-white/[0.03] active:scale-[0.98] transition-all"
                    >
                      <LogIn size={14} />
                      {t.contact.signInToMessage}
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            ) : (
              /* ── Contact Form (logged-in users) ── */
              <motion.form
                key="contact-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="space-y-4"
                onSubmit={handleSubmit}
                noValidate
              >
                {/* Cooldown warning */}
                {cooldown > 0 && (
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400">
                    <AlertTriangle size={16} className="shrink-0" />
                    <p className="text-xs font-medium">
                      {t.contact.rateLimited} ({cooldown}s)
                    </p>
                  </div>
                )}

                {isCustomFrame && artwork && (
                  <div className="p-4 rounded-xl bg-black/[0.03] dark:bg-white/[0.03] border border-black/5 dark:border-white/5 mb-2">
                    <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-1">
                      {t.contact.customFrameSubject}
                    </p>
                    <p className="text-sm font-bold">{localize(artwork.title)}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      placeholder={t.contact.name}
                      value={name}
                      onChange={(e) => { setName(e.target.value.slice(0, MAX_NAME_LEN)); setErrors((p) => ({ ...p, name: '' })); }}
                      className={`${INPUT_CLS} ${errors.name ? 'border-red-400 dark:border-red-400' : ''}`}
                      maxLength={MAX_NAME_LEN}
                      autoComplete="name"
                    />
                    {errors.name && <p className="text-[9px] text-red-500 mt-1 font-medium">{errors.name}</p>}
                  </div>
                  <div>
                    <input
                      type="email"
                      placeholder={t.contact.email}
                      value={email}
                      onChange={(e) => { setEmail(e.target.value.slice(0, MAX_EMAIL_LEN)); setErrors((p) => ({ ...p, email: '' })); }}
                      className={`${INPUT_CLS} ${errors.email ? 'border-red-400 dark:border-red-400' : ''}`}
                      maxLength={MAX_EMAIL_LEN}
                      autoComplete="email"
                    />
                    {errors.email && <p className="text-[9px] text-red-500 mt-1 font-medium">{errors.email}</p>}
                  </div>
                </div>
                <div>
                  <input
                    type="text"
                    placeholder={t.contact.subject}
                    value={subject}
                    onChange={(e) => { setSubject(e.target.value.slice(0, MAX_SUBJECT_LEN)); setErrors((p) => ({ ...p, subject: '' })); }}
                    className={`${INPUT_CLS} ${errors.subject ? 'border-red-400 dark:border-red-400' : ''}`}
                    maxLength={MAX_SUBJECT_LEN}
                  />
                  {errors.subject && <p className="text-[9px] text-red-500 mt-1 font-medium">{errors.subject}</p>}
                </div>
                <div>
                  <textarea
                    placeholder={t.contact.message}
                    rows={5}
                    value={message}
                    onChange={(e) => { setMessage(e.target.value.slice(0, MAX_MESSAGE_LEN)); setErrors((p) => ({ ...p, message: '' })); }}
                    className={`${INPUT_CLS} resize-none ${errors.message ? 'border-red-400 dark:border-red-400' : ''}`}
                    maxLength={MAX_MESSAGE_LEN}
                  />
                  <div className="flex justify-between mt-1">
                    {errors.message ? <p className="text-[9px] text-red-500 font-medium">{errors.message}</p> : <span />}
                    <p className={`text-[9px] ${message.length > MAX_MESSAGE_LEN * 0.9 ? 'text-amber-500' : 'text-black/20 dark:text-white/20'}`}>
                      {message.length}/{MAX_MESSAGE_LEN}
                    </p>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isDisabled}
                  className="w-full py-4 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.3em] rounded-xl hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {cooldown > 0 ? `${t.contact.send} (${cooldown}s)` : t.contact.send}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
